import {
  type Permission,
  Permission as PermissionEnum,
} from '@up2stay/up2promo-api-client';

export type PermissionModule =
  | 'workspace'
  | 'members'
  | 'roles'
  | 'teams'
  | 'leads'
  | 'events'
  | 'profiles'
  | 'products'
  | 'locations'
  | 'integrations'
  | 'reputation'
  | 'billing'
  | 'feedback';

export type PermissionMetadata = {
  permission: Permission;
  module: PermissionModule;
  labelKey: string;
};

/** Mirrored from backend `PERMISSION_METADATA` for matrix grouping. */
export const PERMISSION_METADATA: readonly PermissionMetadata[] = [
  {
    permission: PermissionEnum.WORKSPACE_READ,
    module: 'workspace',
    labelKey: 'permissions.workspace.read',
  },
  {
    permission: PermissionEnum.WORKSPACE_UPDATE,
    module: 'workspace',
    labelKey: 'permissions.workspace.update',
  },
  {
    permission: PermissionEnum.WORKSPACE_DELETE,
    module: 'workspace',
    labelKey: 'permissions.workspace.delete',
  },
  {
    permission: PermissionEnum.WORKSPACE_MANAGE_SETTINGS,
    module: 'workspace',
    labelKey: 'permissions.workspace.manage_settings',
  },
  {
    permission: PermissionEnum.MEMBERS_READ,
    module: 'members',
    labelKey: 'permissions.members.read',
  },
  {
    permission: PermissionEnum.MEMBERS_INVITE,
    module: 'members',
    labelKey: 'permissions.members.invite',
  },
  {
    permission: PermissionEnum.MEMBERS_UPDATE_ROLE,
    module: 'members',
    labelKey: 'permissions.members.update_role',
  },
  {
    permission: PermissionEnum.MEMBERS_REMOVE,
    module: 'members',
    labelKey: 'permissions.members.remove',
  },
  {
    permission: PermissionEnum.ROLES_MANAGE,
    module: 'roles',
    labelKey: 'permissions.roles.manage',
  },
  {
    permission: PermissionEnum.TEAMS_READ,
    module: 'teams',
    labelKey: 'permissions.teams.read',
  },
  {
    permission: PermissionEnum.TEAMS_MANAGE,
    module: 'teams',
    labelKey: 'permissions.teams.manage',
  },
  {
    permission: PermissionEnum.LEADS_READ,
    module: 'leads',
    labelKey: 'permissions.leads.read',
  },
  {
    permission: PermissionEnum.LEADS_READ_OTHERS,
    module: 'leads',
    labelKey: 'permissions.leads.read_others',
  },
  {
    permission: PermissionEnum.LEADS_UPDATE,
    module: 'leads',
    labelKey: 'permissions.leads.update',
  },
  {
    permission: PermissionEnum.LEADS_UPDATE_OTHERS,
    module: 'leads',
    labelKey: 'permissions.leads.update_others',
  },
  {
    permission: PermissionEnum.LEADS_EXPORT,
    module: 'leads',
    labelKey: 'permissions.leads.export',
  },
  {
    permission: PermissionEnum.EVENTS_READ,
    module: 'events',
    labelKey: 'permissions.events.read',
  },
  {
    permission: PermissionEnum.EVENTS_MANAGE,
    module: 'events',
    labelKey: 'permissions.events.manage',
  },
  {
    permission: PermissionEnum.PROFILES_READ,
    module: 'profiles',
    labelKey: 'permissions.profiles.read',
  },
  {
    permission: PermissionEnum.PROFILES_UPDATE,
    module: 'profiles',
    labelKey: 'permissions.profiles.update',
  },
  {
    permission: PermissionEnum.PROFILES_MANAGE_TEMPLATES,
    module: 'profiles',
    labelKey: 'permissions.profiles.manage_templates',
  },
  {
    permission: PermissionEnum.PRODUCTS_READ,
    module: 'products',
    labelKey: 'permissions.products.read',
  },
  {
    permission: PermissionEnum.PRODUCTS_MANAGE,
    module: 'products',
    labelKey: 'permissions.products.manage',
  },
  {
    permission: PermissionEnum.LOCATIONS_READ,
    module: 'locations',
    labelKey: 'permissions.locations.read',
  },
  {
    permission: PermissionEnum.LOCATIONS_MANAGE,
    module: 'locations',
    labelKey: 'permissions.locations.manage',
  },
  {
    permission: PermissionEnum.INTEGRATIONS_READ,
    module: 'integrations',
    labelKey: 'permissions.integrations.read',
  },
  {
    permission: PermissionEnum.INTEGRATIONS_MANAGE,
    module: 'integrations',
    labelKey: 'permissions.integrations.manage',
  },
  {
    permission: PermissionEnum.REPUTATION_READ,
    module: 'reputation',
    labelKey: 'permissions.reputation.read',
  },
  {
    permission: PermissionEnum.REPUTATION_REPLY,
    module: 'reputation',
    labelKey: 'permissions.reputation.reply',
  },
  {
    permission: PermissionEnum.REPUTATION_CONFIGURE,
    module: 'reputation',
    labelKey: 'permissions.reputation.configure',
  },
  {
    permission: PermissionEnum.FEEDBACK_READ,
    module: 'feedback',
    labelKey: 'permissions.feedback.read',
  },
  {
    permission: PermissionEnum.FEEDBACK_INVITE,
    module: 'feedback',
    labelKey: 'permissions.feedback.invite',
  },
  {
    permission: PermissionEnum.FEEDBACK_MANAGE,
    module: 'feedback',
    labelKey: 'permissions.feedback.manage',
  },
  {
    permission: PermissionEnum.BILLING_READ,
    module: 'billing',
    labelKey: 'permissions.billing.read',
  },
  {
    permission: PermissionEnum.BILLING_MANAGE,
    module: 'billing',
    labelKey: 'permissions.billing.manage',
  },
] as const;

/** Permissions that must never appear in a custom workspace role. */
export const CUSTOM_ROLE_FORBIDDEN_PERMISSIONS: readonly Permission[] = [
  PermissionEnum.WORKSPACE_DELETE,
];

/**
 * Mirrored from backend `BASELINE_PERMISSIONS`. Every active membership gets
 * these regardless of its role, so the matrix renders them as locked-on.
 */
export const CUSTOM_ROLE_BASELINE_PERMISSIONS: readonly Permission[] = [
  PermissionEnum.WORKSPACE_READ,
  PermissionEnum.PROFILES_READ,
  PermissionEnum.PROFILES_UPDATE,
];

export const PERMISSION_MODULES: readonly PermissionModule[] = [
  'workspace',
  'members',
  'roles',
  'teams',
  'leads',
  'events',
  'profiles',
  'products',
  'locations',
  'integrations',
  'reputation',
  'feedback',
  'billing',
];

export function groupPermissionsByModule(
  permissions: readonly PermissionMetadata[],
): Map<PermissionModule, PermissionMetadata[]> {
  const grouped = new Map<PermissionModule, PermissionMetadata[]>();
  for (const entry of permissions) {
    const existing = grouped.get(entry.module) ?? [];
    grouped.set(entry.module, [...existing, entry]);
  }
  return grouped;
}

/** Compile-time: every api-client `Permission` must appear in PERMISSION_METADATA. */
type PermissionMetadataCoverage =
  (typeof PERMISSION_METADATA)[number]['permission'];
type MissingPermissionMetadata = Exclude<
  Permission,
  PermissionMetadataCoverage
>;
type AssertAllPermissionsHaveMetadata = [MissingPermissionMetadata] extends [
  never,
]
  ? true
  : MissingPermissionMetadata;
const _assertAllPermissionsHaveMetadata: AssertAllPermissionsHaveMetadata = true;
void _assertAllPermissionsHaveMetadata;
